import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Users, MessageSquare, Target, TrendingUp, Calendar,
  Activity, Clock, UserPlus, Loader2, ShieldAlert
} from 'lucide-react';
import { format } from 'date-fns';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const Admin = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => {
    const checkAdminAndFetch = async () => {
      try {
        // Check if user is admin
        const adminCheck = await api.get('/api/admin/check');
        if (!adminCheck.data.is_admin) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        setIsAdmin(true);

        // Fetch all admin data
        const [statsRes, usersRes, chartRes] = await Promise.all([
          api.get('/api/admin/stats'),
          api.get('/api/admin/users?limit=20'),
          api.get('/api/admin/registrations?days=14')
        ]);

        setStats(statsRes.data);
        setUsers(usersRes.data.users);
        setTotalUsers(usersRes.data.total);
        setChartData(chartRes.data.data);
      } catch (error) {
        console.error('Admin fetch error:', error);
        if (error.response?.status === 403) {
          setIsAdmin(false);
        }
      } finally {
        setLoading(false);
      }
    };

    checkAdminAndFetch();
  }, [api]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-atlasCyan animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <ShieldAlert className="w-16 h-16 text-atlasRed mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
        <p className="text-atlasTextSecondary mb-6">You don't have permission to access this page.</p>
        <button 
          onClick={() => navigate('/dashboard')}
          className="btn-primary"
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="p-6 lg:p-8 space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Admin Dashboard</h1>
        <p className="text-atlasTextSecondary">Monitor ATLAS usage and user activity</p>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-atlasCyan/10 rounded-lg">
              <Users className="w-5 h-5 text-atlasCyan" />
            </div>
            <span className="text-atlasTextSecondary text-sm">Total Users</span>
          </div>
          <p className="text-3xl font-bold text-white atlas-mono" data-testid="total-users">
            {stats?.users?.total || 0}
          </p>
          <p className="text-green-500 text-sm mt-1">
            +{stats?.users?.last_7d || 0} this week
          </p>
        </div>

        <div className="glass-card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-atlasYellow/10 rounded-lg">
              <UserPlus className="w-5 h-5 text-atlasYellow" />
            </div>
            <span className="text-atlasTextSecondary text-sm">New Today</span>
          </div>
          <p className="text-3xl font-bold text-white atlas-mono" data-testid="users-today">
            {stats?.users?.last_24h || 0}
          </p>
          <p className="text-atlasTextSecondary text-sm mt-1">registrations</p>
        </div>

        <div className="glass-card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-atlasPurple/10 rounded-lg">
              <Activity className="w-5 h-5 text-atlasPurple" />
            </div>
            <span className="text-atlasTextSecondary text-sm">Active (24h)</span>
          </div>
          <p className="text-3xl font-bold text-white atlas-mono" data-testid="active-users">
            {stats?.active_users?.last_24h || 0}
          </p>
          <p className="text-atlasTextSecondary text-sm mt-1">users chatting</p>
        </div>

        <div className="glass-card p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <MessageSquare className="w-5 h-5 text-green-500" />
            </div>
            <span className="text-atlasTextSecondary text-sm">Messages</span>
          </div>
          <p className="text-3xl font-bold text-white atlas-mono" data-testid="total-messages">
            {stats?.engagement?.total_messages || 0}
          </p>
          <p className="text-atlasTextSecondary text-sm mt-1">total sent</p>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="glass-card p-4 text-center">
          <p className="text-2xl font-bold text-atlasCyan atlas-mono">
            {stats?.engagement?.total_conversations || 0}
          </p>
          <p className="text-xs text-atlasTextSecondary mt-1">Conversations</p>
        </div>
        <div className="glass-card p-4 text-center">
          <p className="text-2xl font-bold text-atlasYellow atlas-mono">
            {stats?.engagement?.total_decisions || 0}
          </p>
          <p className="text-xs text-atlasTextSecondary mt-1">Decisions</p>
        </div>
        <div className="glass-card p-4 text-center">
          <p className="text-2xl font-bold text-atlasPurple atlas-mono">
            {stats?.engagement?.total_commitments || 0}
          </p>
          <p className="text-xs text-atlasTextSecondary mt-1">Commitments</p>
        </div>
        <div className="glass-card p-4 text-center">
          <p className="text-2xl font-bold text-green-500 atlas-mono">
            {stats?.engagement?.total_priorities || 0}
          </p>
          <p className="text-xs text-atlasTextSecondary mt-1">Priorities</p>
        </div>
        <div className="glass-card p-4 text-center">
          <p className="text-2xl font-bold text-white atlas-mono">
            {stats?.active_users?.last_7d || 0}
          </p>
          <p className="text-xs text-atlasTextSecondary mt-1">Active (7d)</p>
        </div>
        <div className="glass-card p-4 text-center">
          <p className="text-2xl font-bold text-white atlas-mono">
            {stats?.users?.last_30d || 0}
          </p>
          <p className="text-xs text-atlasTextSecondary mt-1">New (30d)</p>
        </div>
      </div>

      {/* Chart and Users Table */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Registration Chart */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-atlasCyan" />
            Registrations (Last 14 Days)
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1A1A2E" />
                <XAxis 
                  dataKey="date" 
                  stroke="#A1A1AA" 
                  fontSize={10}
                  tickFormatter={(value) => format(new Date(value), 'MMM d')}
                />
                <YAxis stroke="#A1A1AA" fontSize={10} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1A1A2E', 
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px'
                  }}
                  labelStyle={{ color: '#fff' }}
                  itemStyle={{ color: '#00D9FF' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#00D9FF" 
                  strokeWidth={2}
                  dot={{ fill: '#00D9FF', strokeWidth: 0, r: 4 }}
                  activeDot={{ r: 6, fill: '#00D9FF' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Users */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-atlasCyan" />
            Recent Users ({totalUsers} total)
          </h3>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-atlasCyan to-atlasPurple flex items-center justify-center">
                    <span className="text-xs font-bold text-atlasBg">
                      {u.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{u.name}</p>
                    <p className="text-atlasTextSecondary text-xs">{u.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-atlasTextSecondary">
                    {u.conversations} chats · {u.decisions} decisions
                  </p>
                  <p className="text-xs text-atlasTextSecondary flex items-center gap-1 justify-end">
                    <Clock className="w-3 h-3" />
                    {format(new Date(u.created_at), 'MMM d, yyyy')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
